import React, {useCallback, useLayoutEffect, useRef, RefObject} from 'react';
import ReactQuill from 'react-quill';
import {handlePaste as handlePasteUtil} from '../../textEditor/TextEditorHandlers.js';
import {convertTextBeforeSave, decodeHtmlEntities} from '../../textEditor/TextEditorHelpers.js';

type QuillRef = RefObject<ReactQuill | null>;

function escapeHtmlLine(line: string): string {
    return line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Plain text (with \\n) or legacy HTML for the free message field.
 */
export function normalizeFreeMessageForEditor(value: string): string {
    if(!value) return '';
    const trimmed = value.trim();
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(trimmed);
    if(looksLikeHtml) {
        return value;
    }
    return value.replace(/\r\n/g, '\n').split('\n').map(escapeHtmlLine).join('<br>');
}

interface UseFreeMessageEditorProps {
    content: string;
    quillRef: QuillRef;
    onChange?: (content: string) => void;
    /** When set, used instead of normalizeFreeMessageForEditor when syncing content into Quill */
    mapContentToEditorHtml?: (content: string) => string;
    /** When set, paste is limited/sanitized like TextEditor (plain + max length) */
    maxPasteChars?: number;
}

export function useFreeMessageEditor({
    content,
    quillRef,
    onChange,
    mapContentToEditorHtml,
    maxPasteChars,
}: UseFreeMessageEditorProps) {
    const lastExternalContentRef = useRef<string | undefined>(undefined);
    const prevMapperRef = useRef(mapContentToEditorHtml);

    useLayoutEffect(() => {
        if(prevMapperRef.current !== mapContentToEditorHtml) {
            if(mapContentToEditorHtml) {
                lastExternalContentRef.current = undefined;
            }
            prevMapperRef.current = mapContentToEditorHtml;
        }

        const reactQuill = quillRef.current;
        if(!reactQuill) return;
        const quill = reactQuill.getEditor();

        if(quill.hasFocus()) {
            lastExternalContentRef.current = content;
            return;
        }

        if(lastExternalContentRef.current === content) {
            return;
        }

        lastExternalContentRef.current = content;
        const normalized = mapContentToEditorHtml
            ? mapContentToEditorHtml(content)
            : normalizeFreeMessageForEditor(content);
        const htmlToSet = normalized.trim() === '' ? '<p><br></p>' : normalized;
        quill.setContents(quill.clipboard.convert(htmlToSet), 'silent');
    }, [content, quillRef, mapContentToEditorHtml]);

    const handlePasteCapture = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            const reactQuill = quillRef.current;
            if(!reactQuill) return;
            const quill = reactQuill.getEditor();
            if(maxPasteChars != null) {
                handlePasteUtil(e.nativeEvent, quillRef, maxPasteChars);
                return;
            }
            e.preventDefault();
            const text = e.clipboardData?.getData('text/plain') ?? '';
            if(text === '') return;
            const range = quill.getSelection(true);
            if(!range) return;
            const plain = text.replace(/\r\n/g, '\n');
            quill.deleteText(range.index, range.length, 'user');
            quill.insertText(range.index, plain, 'user');
        },
        [quillRef, maxPasteChars],
    );

    const handleTextChange = useCallback(
        (_html: string, _delta: unknown, source: string) => {
            if(source !== 'user') return;
            if(!quillRef.current) return;
            const serialized = decodeHtmlEntities(convertTextBeforeSave(quillRef));
            onChange?.(serialized);
        },
        [quillRef, onChange],
    );

    const handleBlur = useCallback(() => {
        const reactQuill = quillRef.current;
        if(!reactQuill) return;
        const quill = reactQuill.getEditor();
        if(quill.getText().trim() === '') {
            onChange?.('');
        }
    }, [quillRef, onChange]);

    return {
        handleTextChange,
        handleBlur,
        handlePasteCapture,
    };
}
